/* Murmure — canevas de l'éditeur de stickers : calques, sélection, manipulation.
 *
 * Le canevas tient le document que le serveur composera. Sa géométrie est exprimée en
 * FRACTIONS de l'encombrement, jamais en pixels : l'écran travaille sur trois cents
 * pixels, l'impression sur près de trois mille, et seule cette convention garantit que
 * les deux montrent la même chose. Chaque calque est repéré par son CENTRE, comme le
 * `transform-origin` de l'aperçu — sinon rotation et redimensionnement ne tomberaient
 * pas au même endroit de part et d'autre.
 *
 * Le texte n'est jamais du texte : dès la frappe, opentype.js le convertit en tracés
 * vectoriels, et ce sont ces tracés que l'aperçu affiche et que le serveur imprime.
 * Le même chemin des deux côtés, donc aucun écart de crénage possible — un décalage de
 * glyphe ne se verrait pas à l'écran et se paierait sur cinq cents exemplaires.
 *
 * Aucune dépendance : ce module ne connaît ni le tarif, ni le panier, ni l'API. Il
 * publie MurmureCanevas et prévient son appelant à chaque changement.
 */
(function () {
  "use strict";

  var CATALOGUE_POLICES = [
    { id: "inter", nom: "Inter" },
    { id: "montserrat", nom: "Montserrat" },
    { id: "bebas", nom: "Bebas Neue" },
    { id: "anton", nom: "Anton" },
    { id: "playfair", nom: "Playfair" },
    { id: "righteous", nom: "Righteous" },
    { id: "lobster", nom: "Lobster" },
    { id: "pacifico", nom: "Pacifico" },
    { id: "caveat", nom: "Caveat" },
    { id: "marker", nom: "Marker" },
    { id: "bangers", nom: "Bangers" },
    { id: "mono", nom: "Space Mono" }
  ];

  // Repère de vectorisation. Mille unités par cadratin donnent au dixième près une
  // précision de 0,03 mm sur un sticker de 24 cm : on peut n'écrire qu'une décimale
  // dans les tracés, ce qui les allège de moitié.
  var EM = 1000;
  var DECIMALES = 1;
  var INTERLIGNE = 1.18;

  // Bornes de manipulation. Un calque peut déborder de l'encombrement — c'est même la
  // règle sur un fond en pleine page — mais pas au point de disparaître ni d'exploser.
  var MIN_COTE = 0.02;
  var MAX_COTE = 4;
  var AIMANT_ROTATION = 4; // degrés : au-delà, l'angle reste libre

  var SVG_NS = "http://www.w3.org/2000/svg";

  /* ---------------------------------------------------------------- Polices */

  var opentypePret = null;
  var polices = {};

  function chargerOpentype(url) {
    if (opentypePret) return opentypePret;
    opentypePret = new Promise(function (ok, ko) {
      if (window.opentype) return ok(window.opentype);
      var s = document.createElement("script");
      s.src = url;
      s.onload = function () {
        window.opentype ? ok(window.opentype) : ko(new Error("opentype_absent"));
      };
      s.onerror = function () { ko(new Error("opentype_injoignable")); };
      document.head.appendChild(s);
    });
    return opentypePret;
  }

  /** Charge et analyse une police. Le résultat est gardé : on ne la relit qu'une fois. */
  function chargerPolice(id, urls) {
    if (polices[id]) return polices[id];
    polices[id] = chargerOpentype(urls.opentype)
      .then(function (ot) {
        if (!urls.polices[id]) throw new Error("police_inconnue");
        return fetch(urls.polices[id]).then(function (r) {
          if (!r.ok) throw new Error("police_injoignable");
          return r.arrayBuffer();
        }).then(function (buf) { return ot.parse(buf); });
      })
      .catch(function (e) {
        // Sans quoi une panne réseau passagère condamnerait la police pour toute la
        // visite : on oublie l'échec, le prochain essai repartira de zéro.
        delete polices[id];
        throw e;
      });
    return polices[id];
  }

  /**
   * Vectorise un texte : tracé unique, et boîte dans laquelle il s'inscrit.
   *
   * La hauteur est calculée sur les métriques de la police, pas sur l'encre : sinon
   * « Paris » et « Lyon » n'auraient pas la même taille apparente, et le calque
   * sauterait à chaque lettre à jambage frappée.
   */
  function vectoriser(police, texte, alignement) {
    var lignes = String(texte == null ? "" : texte).split("\n");
    var echelle = EM / police.unitsPerEm;
    var monte = police.ascender * echelle;
    var descend = -police.descender * echelle;
    var hauteurLigne = (monte + descend) * INTERLIGNE;

    var largeurs = lignes.map(function (l) {
      return police.getAdvanceWidth(l || " ", EM);
    });
    var largeur = Math.max.apply(null, largeurs.concat([1]));

    var morceaux = [];
    lignes.forEach(function (ligne, i) {
      if (!ligne) return;
      var decalage = alignement === "droite" ? largeur - largeurs[i]
        : alignement === "centre" ? (largeur - largeurs[i]) / 2 : 0;
      var chemin = police.getPath(ligne, decalage, monte + i * hauteurLigne, EM);
      var d = chemin.toPathData(DECIMALES);
      if (d) morceaux.push(d);
    });

    return {
      chemin: morceaux.join(" "),
      cheminL: Math.max(1, Math.round(largeur)),
      cheminH: Math.max(1, Math.round(hauteurLigne * (lignes.length - 1) + monte + descend))
    };
  }

  /* -------------------------------------------------------------- Géométrie */

  var idSuivant = 1;
  function nouvelId() { return "c" + (idSuivant++); }
  var borne = function (v, min, max) { return Math.min(Math.max(v, min), max); };

  /* ---------------------------------------------------------------- Canevas */

  /**
   * @param {object} o
   * @param {HTMLElement} o.scene   conteneur de l'aperçu, au rapport de l'encombrement
   * @param {object} o.urls         { opentype, polices: { id: url } }
   * @param {function} o.onChange      appelé après toute modification du document
   * @param {function} o.onSelect      appelé quand la sélection change
   * @param {function} o.onHistorique  appelé quand annuler/refaire changent de portée
   */
  function creer(o) {
    var scene = o.scene;
    var urls = o.urls;
    var prevenir = o.onChange || function () {};
    var signalerSelection = o.onSelect || function () {};

    var calques = [];
    var selectionId = null;
    var ratio = 1;          // largeur / hauteur de l'encombrement
    var fond = null;
    var sources = {};       // srcId -> { url, largeur, hauteur }

    function trouver(id) {
      for (var i = 0; i < calques.length; i++) if (calques[i].id === id) return calques[i];
      return null;
    }
    function indexDe(id) {
      for (var i = 0; i < calques.length; i++) if (calques[i].id === id) return i;
      return -1;
    }

    /* --------------------------------------------------------- Historique */

    /*
     * Annuler et refaire portent sur le DOCUMENT — calques et fond — et sur rien
     * d'autre. La proportion de l'encombrement en est exclue à dessein : elle est
     * commandée par les pastilles de la fiche, et la voir revenir en arrière sans que
     * la pastille bouge serait un mensonge. Les visuels chargés en sont exclus aussi,
     * puisqu'ils ne font que s'accumuler : revenir sur l'ajout d'un calque ne doit pas
     * obliger à retélécharger l'image quand on le refait.
     */
    var MEMOIRE = 60;
    var COALESCENCE_MS = 700;
    var historique = [];
    var curseur = -1;
    var derniereCle = null;
    var dernierInstant = 0;
    var prevenirHistorique = o.onHistorique || function () {};

    function instantane() {
      return JSON.stringify({
        fond: fond,
        selectionId: selectionId,
        calques: calques.map(function (c) {
          var copie = {};
          Object.keys(c).forEach(function (k) { if (k !== "_sale") copie[k] = c[k]; });
          return copie;
        })
      });
    }

    /**
     * Retient l'état courant.
     *
     * `cle` permet de fondre en une seule entrée une rafale de modifications de même
     * nature — la frappe d'un texte, surtout, où chaque lettre produirait sinon un pas
     * d'annulation et où revenir en arrière prendrait trente clics.
     */
    function memoriser(cle) {
      var etat = instantane();
      if (curseur >= 0 && historique[curseur] === etat) return;
      var maintenant = Date.now();
      var fondre = cle && cle === derniereCle && (maintenant - dernierInstant) < COALESCENCE_MS;
      derniereCle = cle || null;
      dernierInstant = maintenant;
      if (fondre && curseur >= 0) {
        historique[curseur] = etat;
        return;
      }
      historique.splice(curseur + 1);
      historique.push(etat);
      if (historique.length > MEMOIRE) historique.shift();
      curseur = historique.length - 1;
      prevenirHistorique();
    }

    function restaurer(etat) {
      var d = JSON.parse(etat);
      fond = d.fond;
      calques = d.calques.map(function (c) { c._sale = true; return c; });
      selectionId = trouver(d.selectionId) ? d.selectionId : null;
      derniereCle = null;
      dessiner();
      signalerSelection(trouver(selectionId));
      prevenir();
      prevenirHistorique();
    }

    function annuler() {
      if (curseur <= 0) return false;
      curseur -= 1;
      restaurer(historique[curseur]);
      return true;
    }

    function refaire() {
      if (curseur < 0 || curseur >= historique.length - 1) return false;
      curseur += 1;
      restaurer(historique[curseur]);
      return true;
    }

    /* ------------------------------------------------------------- Dessin */

    function noeudTexte(c) {
      var svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("viewBox", "0 0 " + c.cheminL + " " + c.cheminH);
      svg.setAttribute("preserveAspectRatio", "none");
      svg.setAttribute("focusable", "false");
      var p = document.createElementNS(SVG_NS, "path");
      p.setAttribute("d", c.chemin);
      p.setAttribute("fill", c.couleur);
      if (c.contour && c.contourEp > 0) {
        p.setAttribute("stroke", c.contour);
        p.setAttribute("stroke-width", String(c.contourEp * c.cheminH));
        p.setAttribute("stroke-linejoin", "round");
        // Même ordre de peinture que le serveur : le contour sous le remplissage,
        // sinon il rognerait la moitié de l'épaisseur sur chaque glyphe.
        p.setAttribute("paint-order", "stroke");
      }
      svg.appendChild(p);
      return svg;
    }

    function noeudImage(c) {
      var img = document.createElement("img");
      var s = sources[c.srcId] || {};
      // Le canevas mesure la silhouette du document dans un canvas : sans en-tête
      // CORS explicite, la lecture des pixels serait refusée et la découpe à la
      // forme perdrait ses dimensions réelles.
      img.crossOrigin = "anonymous";
      img.src = (c.detoure && s.cut) ? s.cut : s.url;
      img.alt = "";
      img.draggable = false;
      return img;
    }

    function dessiner() {
      scene.style.setProperty("--cv-ratio", String(ratio));
      scene.style.background = fond || "";
      scene.setAttribute("data-vide", calques.length ? "false" : "true");

      var existants = {};
      Array.prototype.forEach.call(scene.querySelectorAll("[data-calque]"), function (n) {
        existants[n.getAttribute("data-calque")] = n;
      });

      calques.forEach(function (c, rang) {
        var n = existants[c.id];
        delete existants[c.id];
        if (!n) {
          n = document.createElement("div");
          n.className = "cv__calque";
          n.setAttribute("data-calque", c.id);
          scene.appendChild(n);
          c._sale = true;
        }
        // Le contenu n'est refait que s'il a changé : recréer une image à chaque
        // déplacement la ferait clignoter à chaque frame du glissement.
        if (c._sale) {
          n.innerHTML = "";
          n.appendChild(c.type === "texte" ? noeudTexte(c) : noeudImage(c));
          c._sale = false;
        }
        n.style.left = (c.x * 100) + "%";
        n.style.top = (c.y * 100) + "%";
        n.style.width = (c.l * 100) + "%";
        n.style.height = (c.h * 100) + "%";
        n.style.transform = "translate(-50%,-50%) rotate(" + c.rot + "deg)";
        n.style.opacity = String(c.opacite);
        n.style.zIndex = String(10 + rang);
        n.setAttribute("data-choisi", c.id === selectionId ? "true" : "false");
      });

      Object.keys(existants).forEach(function (id) { existants[id].remove(); });
      poserPoignees();
    }

    /**
     * Pose la poignée de rotation dans le calque choisi, pour qu'elle tourne avec lui.
     *
     * Il n'y a plus de poignées de coin. Tirer un coin semblait régler la taille du
     * sticker, alors que celle-ci est fixée par le format commandé : sur une découpe à
     * la forme, le serveur agrandit de toute façon le motif jusqu'au bord. Le geste ne
     * changeait donc rien à ce qui est reçu, et le zoom du panneau dit ce qu'il fait.
     */
    function poserPoignees() {
      var ancienne = scene.querySelector(".cv__poignees");
      if (ancienne) ancienne.remove();
      if (!selectionId) return;
      var hote = scene.querySelector('[data-calque="' + selectionId + '"]');
      if (!hote) return;

      var boite = document.createElement("div");
      boite.className = "cv__poignees";
      var rot = document.createElement("button");
      rot.type = "button";
      rot.className = "cv__poignee cv__poignee--rot";
      rot.setAttribute("data-rot", "1");
      rot.setAttribute("aria-label", "Faire pivoter");
      boite.appendChild(rot);
      hote.appendChild(boite);
    }

    /* -------------------------------------------------------- Manipulation */

    function centreEcran() {
      var r = scene.getBoundingClientRect();
      return { r: r, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    }

    /** Point écran -> fractions de l'encombrement. */
    function enFractions(e) {
      var r = scene.getBoundingClientRect();
      return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
    }

    var geste = null;

    function commencer(e) {
      var poignee = e.target.closest ? e.target.closest("[data-rot]") : null;
      var noeud = e.target.closest ? e.target.closest("[data-calque]") : null;

      if (!noeud) {
        // Clic dans le vide : on désélectionne. C'est le seul moyen de refermer les
        // poignées au doigt, faute de touche Échap sur mobile.
        if (selectionId) { selectionId = null; dessiner(); signalerSelection(null); }
        return;
      }

      var c = trouver(noeud.getAttribute("data-calque"));
      if (!c) return;
      if (c.id !== selectionId) { selectionId = c.id; dessiner(); signalerSelection(c); }

      var p = enFractions(e);
      geste = {
        calque: c,
        depart: p,
        etat: { x: c.x, y: c.y, l: c.l, h: c.h, rot: c.rot },
        mode: poignee ? "rot" : "deplacer"
      };
      scene.setAttribute("data-geste", geste.mode);
      try { scene.setPointerCapture(e.pointerId); } catch (err) { /* pas capturable */ }
      e.preventDefault();
    }

    function poursuivre(e) {
      if (!geste) return;
      var c = geste.calque;
      var d = geste.etat;
      var p = enFractions(e);

      if (geste.mode === "deplacer") {
        c.x = borne(d.x + (p.x - geste.depart.x), -MAX_COTE, MAX_COTE);
        c.y = borne(d.y + (p.y - geste.depart.y), -MAX_COTE, MAX_COTE);
      } else {
        var ce = centreEcran();
        var px = ce.r.left + d.x * ce.r.width;
        var py = ce.r.top + d.y * ce.r.height;
        // La poignée est au-dessus du calque : +90° pour que l'angle zéro corresponde
        // à un calque droit et non couché.
        var a = Math.atan2(e.clientY - py, e.clientX - px) * 180 / Math.PI + 90;
        var quart = Math.round(a / 90) * 90;
        c.rot = Math.abs(a - quart) < AIMANT_ROTATION ? ((quart % 360) + 360) % 360
          : ((a % 360) + 360) % 360;
      }
      dessiner();
    }

    /**
     * Agrandit ou réduit un calque autour de son centre.
     *
     * Les proportions sont toujours conservées : un texte étiré et une photo écrasée
     * sont des défauts qu'on ne rattrape pas à l'impression. Le centre ne bouge pas,
     * de sorte qu'on peut zoomer sans avoir à replacer ensuite — c'est ce qui manquait
     * aux poignées de coin, où le calque partait de travers à chaque pas.
     */
    function zoomer(id, facteur) {
      var c = trouver(id);
      if (!c) return;
      var f = Number(facteur);
      if (!(f > 0)) return;
      var nl = borne(c.l * f, MIN_COTE, MAX_COTE);
      var nh = borne(c.h * f, MIN_COTE, MAX_COTE);
      // Une seule des deux bornes atteinte déformerait le calque : on s'arrête à la
      // plus contraignante et on garde les proportions intactes.
      var reel = Math.min(nl / c.l, nh / c.h);
      if (Math.abs(reel - 1) < 1e-4) return;
      c.l *= reel;
      c.h *= reel;
      dessiner();
      prevenir();
      memoriser("zoom:" + id);
    }

    /**
     * Part du format qu'occupe un calque, sur son axe dominant.
     *
     * `l` et `h` sont déjà des fractions, chacune de son propre côté : la plus grande
     * des deux dit à quel point le calque remplit le cadre, quelle que soit sa forme.
     */
    function echelle(c) {
      return c ? Math.max(c.l, c.h) : null;
    }

    function finir(e) {
      if (!geste) return;
      var mode = geste.mode;
      var id = geste.calque.id;
      geste = null;
      scene.removeAttribute("data-geste");
      try { scene.releasePointerCapture(e.pointerId); } catch (err) { /* déjà relâché */ }
      prevenir();
      // Un seul pas d'annulation par geste, et non un par frame : c'est la position
      // d'arrivée qui compte, pas le chemin parcouru par la main.
      memoriser(mode + ":" + id);
    }

    scene.addEventListener("pointerdown", commencer);
    scene.addEventListener("pointermove", poursuivre);
    scene.addEventListener("pointerup", finir);
    scene.addEventListener("pointercancel", finir);

    /* ------------------------------------------------------------ Mesures */

    // Côté utile de la toile d'analyse. La mesure est comparée à un rognage fait sur
    // près de 900 px côté serveur, et c'est elle qui annonce au client les dimensions
    // de son sticker découpé. À 220 px, l'écart atteignait presque le millimètre.
    var COTE_ANALYSE = 360;
    // Le liseré déborde de la scène : la toile est plus grande qu'elle, sinon un visuel
    // posé contre le bord verrait son contour coupé net. La même marge sert à placer
    // l'image dans l'aperçu, c'est pourquoi analyser() la rend à l'appelant.
    var MARGE_ANALYSE = 0.18;
    var TRAIT_COUPE = 2;
    // Même valeur que le serveur : en deçà, l'encre ne s'imprime pas et la découpe ne
    // doit pas la suivre. Les visuels détourés traînent souvent une auréole quasi
    // transparente ; sans ce seuil, le liseré contourne un fantôme.
    var SEUIL_ENCRE = 24;
    /*
     * Écart relié entre deux parties du dessin, rapporté au plus grand côté de l'encre.
     *
     * ⛔ MÊME VALEUR QUE LE SERVEUR, qui la justifie en détail : un sticker est une seule
     * pièce, et sans ce pont la découpe cerne chaque lettre à part. Le pont relie les écarts
     * jusqu'à deux fois sa valeur, soit 5 % — les sept écarts du logo d'Affichely font de 2,1
     * à 3,4 %. Il est RELATIF pour que la silhouette soit la même à tous les formats.
     *
     * ⚠️ LES DEUX CHIFFRES DOIVENT RESTER ÉGAUX. Changer celui-ci sans changer celui de
     * catalogue/sticker-render.js ferait promettre à l'aperçu une découpe que la lame ne fait
     * pas, ce que le banc de tests refuse.
     */
    var PONT_RELATIF = 0.025;

    /**
     * Bouche les trous intérieurs d'un masque d'encre, sur place.
     *
     * Un sticker est une pièce d'un seul tenant : la machine suit le contour extérieur
     * et rien d'autre. Le vide sous un guidon ou entre deux rétroviseurs n'est pas un
     * trou à évider, c'est du papier blanc. On inonde le fond depuis les bords de la
     * toile — que la marge d'analyse garantit vides — et ce qu'on n'atteint pas est
     * enclavé, donc à remplir.
     */
    function boucher(px, l, h) {
      var total = l * h;
      var dehors = new Uint8Array(total);
      var pile = new Int32Array(total);
      var n = 0;
      var pousser = function (i) {
        if (dehors[i] || px[i * 4 + 3] !== 0) return;
        dehors[i] = 1;
        pile[n++] = i;
      };
      for (var x = 0; x < l; x++) { pousser(x); pousser((h - 1) * l + x); }
      for (var y = 0; y < h; y++) { pousser(y * l); pousser(y * l + l - 1); }
      while (n > 0) {
        var i = pile[--n];
        var cx = i % l;
        if (cx > 0) pousser(i - 1);
        if (cx < l - 1) pousser(i + 1);
        if (i >= l) pousser(i - l);
        if (i < total - l) pousser(i + l);
      }
      for (var k = 0; k < total; k++) {
        if (dehors[k]) continue;
        px[k * 4] = 255; px[k * 4 + 1] = 255; px[k * 4 + 2] = 255; px[k * 4 + 3] = 255;
      }
    }

    /**
     * Rend le masque franchement binaire, puis bouche ses trous.
     *
     * ⛔ LE FRANC-BORD N'EST PAS UN LUXE. La fermeture empile des dizaines de copies et passe
     * deux fois par le complément : ses bords ressortent à demi opaques. Épaissir un tel masque
     * démultiplie ces demi-teintes, et le trait de coupe sortait hachuré, avec des mouchetures
     * blanches jusque dans les lettres — vu au banc sur un 4 cm. Un masque de découpe ne connaît
     * que deux états : il y a du sticker, ou il n'y en a pas.
     *
     * L'inondation en profite : elle prend tout alpha non nul pour du dessin, si bien qu'une
     * poussière à demi transparente lui barrait le passage et laissait une poche non bouchée.
     */
    function boucherToile(masque) {
      var ctx = masque.getContext("2d");
      if (!ctx) return masque;
      try {
        var img = ctx.getImageData(0, 0, masque.width, masque.height);
        var d = img.data;
        for (var i = 0; i < d.length; i += 4) {
          var plein = d[i + 3] >= 128;
          d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
          d[i + 3] = plein ? 255 : 0;
        }
        boucher(d, masque.width, masque.height);
        ctx.putImageData(img, 0, 0);
      } catch (e) {
        // Toile teintée : on préfère une silhouette trouée à pas de silhouette du tout.
      }
      return masque;
    }

    /** Charge les visuels de tous les calques image. Un échec rend null, pas une erreur. */
    function chargerVisuels() {
      return Promise.all(calques.map(function (c) {
        if (c.type !== "image") return Promise.resolve(null);
        var s = sources[c.srcId] || {};
        var src = (c.detoure && s.cut) ? s.cut : s.url;
        return new Promise(function (ok) {
          var img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = function () { ok(img); };
          img.onerror = function () { ok(null); };
          img.src = src;
        });
      }));
    }

    /**
     * Épaissit une silhouette d'un rayon donné, et la teinte.
     *
     * Le navigateur ne sait pas dilater une image ; on la redessine sur un cercle
     * d'offsets, assez serré pour que deux copies voisines se recouvrent. Le résultat
     * est l'enveloppe exacte à un pixel près, arrondie comme une vraie découpe — là où
     * les drop-shadow empilés du CSS ne donnaient qu'un carré, 40 % trop épais dans
     * les diagonales.
     */
    function epaissir(masque, rayon, couleur) {
      var toile = document.createElement("canvas");
      toile.width = masque.width;
      toile.height = masque.height;
      var ctx = toile.getContext("2d");
      if (!ctx) return toile;
      if (rayon > 0) {
        var n = Math.max(12, Math.min(48, Math.ceil(Math.PI * rayon)));
        for (var i = 0; i < n; i++) {
          var a = (i / n) * Math.PI * 2;
          ctx.drawImage(masque, Math.cos(a) * rayon, Math.sin(a) * rayon);
        }
      }
      ctx.drawImage(masque, 0, 0);
      ctx.globalCompositeOperation = "source-in";
      ctx.fillStyle = couleur;
      ctx.fillRect(0, 0, toile.width, toile.height);
      return toile;
    }

    /**
     * Rend le complément d'un masque : le vide devient plein, le plein devient vide.
     *
     * Il sert à éroder, faute de mieux : le navigateur ne sait pas plus éroder que dilater.
     */
    function complementer(masque) {
      var toile = document.createElement("canvas");
      toile.width = masque.width;
      toile.height = masque.height;
      var ctx = toile.getContext("2d");
      if (!ctx) return toile;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, toile.width, toile.height);
      ctx.globalCompositeOperation = "destination-out";
      ctx.drawImage(masque, 0, 0);
      return toile;
    }

    /**
     * Érode un masque de `rayon` pixels : c'est la dilatation de son complément.
     *
     * Le bord de la toile appartient au complément, ce qui est exact — c'est bien du vide, et
     * l'érosion doit y mordre. La marge d'analyse garantit que le dessin ne le touche pas.
     */
    function eroder(masque, rayon) {
      if (rayon <= 0) return masque;
      return complementer(epaissir(complementer(masque), rayon, "#ffffff"));
    }

    /**
     * Relie les parties voisines du dessin : une fermeture morphologique.
     *
     * ⛔ UN STICKER EST UNE SEULE PIÈCE. Sans ce pont, la découpe cerne chaque lettre à part et
     * le client recevrait son mot en morceaux. Dilater puis éroder du même rayon fait franchir
     * au contour les écarts plus étroits que deux rayons SANS l'épaissir : le bord revient où
     * il était, seuls les vides étroits restent comblés. Une simple dilatation, elle, aurait
     * grossi le liseré pour recoller les lettres.
     *
     * ⚠️ ON NE REPOSE PAS LE DESSIN D'ORIGINE PAR-DESSUS, là où le serveur le fait. Lui érode par
     * flou et seuil, une approximation qui mord trop et rapetissait le sticker ; il doit donc
     * réunir la forme de départ à son résultat. Ici la dilatation est exacte — un vrai disque
     * tamponné — et la fermeture contient déjà le dessin. Réunir quand même mettait deux bords à
     * un demi-pixel l'un de l'autre, et le trait de coupe ressortait hachuré comme une couture.
     *
     * ⚠️ LE RAYON EST CELUI DU SERVEUR, À LA MÊME FRACTION DE L'ENCRE. Les deux doivent voir la
     * même silhouette, sinon l'aperçu promet une découpe que la lame ne fera pas.
     */
    function fermer(masque, rayon) {
      if (rayon <= 0) return masque;
      return eroder(epaissir(masque, rayon, "#ffffff"), rayon);
    }

    /**
     * Rend la composition hors écran et en tire ce que l'écran ne sait pas donner :
     * la boîte de l'encre, et l'image du liseré.
     *
     * Une découpe à la forme suit la silhouette : le serveur rogne la composition puis
     * l'inscrit dans l'encombrement commandé. Sans cette mesure, l'aperçu montrerait
     * autre chose que le sticker livré, et les dimensions annoncées seraient fausses.
     *
     * @param {number} liserRelatif  largeur du liseré rapportée au côté utile du
     *   sticker fini. C'est un rapport et non une longueur, parce que le serveur
     *   agrandit le motif pour remplir le format : un dessin minuscule à l'écran
     *   ressortira énorme, et son liseré paraîtra d'autant plus fin.
     */
    function analyser(liserRelatif) {
      var l = COTE_ANALYSE, h = Math.round(COTE_ANALYSE / ratio);
      var mx = Math.round(l * MARGE_ANALYSE), my = Math.round(h * MARGE_ANALYSE);
      var toile = document.createElement("canvas");
      toile.width = l + 2 * mx;
      toile.height = h + 2 * my;
      var ctx = toile.getContext("2d");
      if (!ctx || !calques.length) return Promise.resolve(null);

      return chargerVisuels().then(function (images) {
        ctx.save();
        ctx.translate(mx, my);
        // Écrêtage au format commandé. Le serveur compose dans une image aux mesures
        // de l'encombrement : ce qui déborde n'existe pas. Sans cette coupe, un calque
        // tiré hors du cadre gonflerait la silhouette, et l'éditeur annoncerait des
        // dimensions que le sticker n'aura pas.
        ctx.beginPath();
        ctx.rect(0, 0, l, h);
        ctx.clip();
        if (fond) { ctx.fillStyle = fond; ctx.fillRect(0, 0, l, h); }
        calques.forEach(function (c, i) {
          ctx.save();
          ctx.globalAlpha = c.opacite;
          ctx.translate(c.x * l, c.y * h);
          ctx.rotate(c.rot * Math.PI / 180);
          var cl = c.l * l, ch = c.h * h;
          ctx.translate(-cl / 2, -ch / 2);
          if (c.type === "image") {
            if (images[i]) ctx.drawImage(images[i], 0, 0, cl, ch);
          } else if (window.Path2D) {
            ctx.scale(cl / c.cheminL, ch / c.cheminH);
            ctx.fillStyle = c.couleur;
            ctx.fill(new Path2D(c.chemin));
          }
          ctx.restore();
        });
        ctx.restore();

        var px;
        try {
          px = ctx.getImageData(0, 0, toile.width, toile.height).data;
        } catch (e) {
          // Toile teintée : un visuel a été servi sans en-tête CORS. On préfère ne
          // rien annoncer plutôt qu'une dimension inventée.
          return null;
        }
        // Masque d'encre : l'alpha ramené à tout ou rien au seuil du serveur. Il sert
        // à la fois de boîte de mesure et de forme à épaissir, si bien que l'aperçu
        // montre la découpe que la lame suivra, et rien d'autre.
        var x1 = toile.width, y1 = toile.height, x2 = -1, y2 = -1;
        for (var y = 0; y < toile.height; y++) {
          for (var x = 0; x < toile.width; x++) {
            var i = (y * toile.width + x) * 4;
            if (px[i + 3] < SEUIL_ENCRE) { px[i + 3] = 0; continue; }
            px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; px[i + 3] = 255;
            if (x < x1) x1 = x;
            if (x > x2) x2 = x;
            if (y < y1) y1 = y;
            if (y > y2) y2 = y;
          }
        }
        if (x2 < 0) return null;

        // La boîte est celle de l'ENCRE, mesurée avant qu'on relie ou qu'on bouche : c'est
        // elle qui dit les dimensions annoncées et la finesse du fichier. Ni le pont ni
        // l'inondation ne la changent, tous deux ne remplissant que des vides intérieurs.
        var boite = {
          x: (x1 - mx) / l, y: (y1 - my) / h,
          l: (x2 - x1 + 1) / l, h: (y2 - y1 + 1) / h
        };

        var apercu = null;
        var contour = null;
        if (liserRelatif != null) {
          var masque = document.createElement("canvas");
          masque.width = toile.width;
          masque.height = toile.height;
          masque.getContext("2d").putImageData(
            new ImageData(px, toile.width, toile.height), 0, 0);
          // Le rayon se mesure sur l'encre, pas sur la scène : c'est l'encre que le
          // serveur ramène à la taille du sticker.
          var encre = Math.max(boite.l * l, boite.h * h);
          // ⛔ RELIER, PUIS BOUCHER, PUIS DILATER. Chaque ordre compte : le pont entre deux
          // lettres crée une poche fermée que l'inondation doit ensuite remplir, et dilater
          // avant de boucher ferait inonder une forme déjà grossie, pour rien.
          masque = boucherToile(fermer(masque, Math.round(PONT_RELATIF * encre)));
          var rayon = Math.max(0, liserRelatif) * encre;
          var blanc = epaissir(masque, rayon, "#ffffff");
          var trait = epaissir(masque, rayon + TRAIT_COUPE, "rgba(181,113,63,.55)");
          var fusion = document.createElement("canvas");
          fusion.width = toile.width;
          fusion.height = toile.height;
          var fc = fusion.getContext("2d");
          fc.drawImage(trait, 0, 0);
          fc.drawImage(blanc, 0, 0);
          try {
            apercu = { url: fusion.toDataURL("image/png"), marge: MARGE_ANALYSE };
            // La silhouette SEULE, sans le trait de coupe : elle sert de masque au
            // papier, qui doit s'arrêter exactement au bord du sticker. Le liseré ne
            // peut pas jouer ce rôle, son trait déborde de TRAIT_COUPE au-delà de la
            // coupe — un papier holographique masqué par lui dépasserait du sticker.
            // C'est bien l'ALPHA qui est utilisé côté CSS, pas le blanc : ce canvas est
            // rempli de blanc opaque, sa transparence est donc la forme.
            contour = { url: blanc.toDataURL("image/png"), marge: MARGE_ANALYSE };
          } catch (e) { apercu = null; contour = null; }
        }

        return { boite: boite, lisere: apercu, silhouette: contour };
      });
    }

    /* -------------------------------------------------------------- Public */

    function ajouterImage(srcId, infos) {
      sources[srcId] = infos;
      // Le visuel entre en occupant les trois quarts de l'encombrement, sans
      // déformation : c'est un point de départ, pas une mise en page.
      var rImg = (infos.largeur && infos.hauteur) ? infos.largeur / infos.hauteur : 1;
      var l = 0.75, h = (0.75 / rImg) * ratio;
      if (h > 0.75) { h = 0.75; l = (0.75 * rImg) / ratio; }
      var c = {
        id: nouvelId(), type: "image", srcId: srcId, detoure: false,
        x: 0.5, y: 0.5, l: l, h: h, rot: 0, opacite: 1, _sale: true
      };
      calques.push(c);
      selectionId = c.id;
      dessiner();
      signalerSelection(c);
      prevenir();
      memoriser();
      return c;
    }

    function ajouterTexte(texte, reglages) {
      var c = {
        id: nouvelId(), type: "texte",
        contenu: texte || "",
        police: (reglages && reglages.police) || "bebas",
        alignement: (reglages && reglages.alignement) || "centre",
        couleur: (reglages && reglages.couleur) || "#111111",
        contour: (reglages && reglages.contour) || null,
        contourEp: (reglages && reglages.contourEp) || 0,
        chemin: "", cheminL: 1, cheminH: 1,
        x: 0.5, y: 0.5, l: 0.6, h: 0.18, rot: 0, opacite: 1, _sale: true
      };
      calques.push(c);
      selectionId = c.id;
      return revectoriser(c).then(function () {
        signalerSelection(c);
        prevenir();
        memoriser();
        return c;
      });
    }

    /**
     * Refait le tracé d'un calque de texte après une frappe ou un changement de police.
     *
     * La hauteur est conservée et la largeur recalculée : le texte grandit autour de
     * son centre, sans se déformer. Fixer la largeur produirait l'inverse — un texte
     * qui rapetisse à mesure qu'on l'écrit.
     */
    function revectoriser(c) {
      return chargerPolice(c.police, urls).then(function (police) {
        var v = vectoriser(police, c.contenu, c.alignement);
        c.chemin = v.chemin;
        c.cheminL = v.cheminL;
        c.cheminH = v.cheminH;
        c.l = borne(c.h * (v.cheminL / v.cheminH) / ratio, MIN_COTE, MAX_COTE);
        c._sale = true;
        dessiner();
        return c;
      });
    }

    function modifier(id, champs) {
      var c = trouver(id);
      if (!c) return Promise.resolve(null);
      var revectoriserApres = false;
      var frappe = false;
      Object.keys(champs).forEach(function (k) {
        if (c[k] === champs[k]) return;
        c[k] = champs[k];
        if (k === "contenu" || k === "police" || k === "alignement") revectoriserApres = true;
        if (k === "contenu") frappe = true;
        if (k === "couleur" || k === "contour" || k === "contourEp") c._sale = true;
      });
      // La frappe se fond en un seul pas d'annulation ; changer de police ou de
      // couleur reste un geste distinct, qu'on veut pouvoir défaire seul.
      var cle = frappe ? "frappe:" + id : null;
      if (revectoriserApres) {
        return revectoriser(c).then(function () { prevenir(); memoriser(cle); return c; });
      }
      dessiner();
      prevenir();
      memoriser(cle);
      return Promise.resolve(c);
    }

    function supprimer(id) {
      var i = indexDe(id);
      if (i < 0) return;
      calques.splice(i, 1);
      if (selectionId === id) selectionId = null;
      dessiner();
      signalerSelection(trouver(selectionId));
      prevenir();
      memoriser();
    }

    function dupliquer(id) {
      var c = trouver(id);
      if (!c) return;
      var copie = JSON.parse(JSON.stringify(c));
      copie.id = nouvelId();
      // Décalée, sinon la copie disparaîtrait sous l'original et le client la
      // croirait perdue.
      copie.x += 0.04;
      copie.y += 0.04;
      copie._sale = true;
      calques.splice(indexDe(id) + 1, 0, copie);
      selectionId = copie.id;
      dessiner();
      signalerSelection(copie);
      prevenir();
      memoriser();
    }

    function deplacerRang(id, sens) {
      var i = indexDe(id);
      if (i < 0) return;
      var j = borne(i + sens, 0, calques.length - 1);
      if (i === j) return;
      calques.splice(j, 0, calques.splice(i, 1)[0]);
      dessiner();
      prevenir();
      memoriser();
    }

    /** Le document tel que le serveur l'attend, débarrassé de l'état d'écran. */
    function exporter() {
      return {
        version: 1,
        proportion: ratio > 1.05 ? "4:3" : ratio < 0.95 ? "3:4" : "1:1",
        fond: fond,
        calques: calques.map(function (c) {
          var base = {
            type: c.type, x: c.x, y: c.y, l: c.l, h: c.h,
            rot: c.rot, opacite: c.opacite
          };
          if (c.type === "image") {
            base.srcId = c.srcId;
            base.detoure = c.detoure;
          } else {
            base.contenu = c.contenu;
            base.chemin = c.chemin;
            base.cheminL = c.cheminL;
            base.cheminH = c.cheminH;
            base.couleur = c.couleur;
            base.contour = c.contour;
            base.contourEp = c.contourEp;
          }
          return base;
        })
      };
    }

    // Le document vide est le premier pas de l'historique : sans lui, on ne pourrait
    // pas annuler l'ajout du tout premier calque.
    memoriser();

    return {
      polices: CATALOGUE_POLICES,
      chargerPolice: function (id) { return chargerPolice(id, urls); },
      vectoriser: vectoriser,

      ajouterImage: ajouterImage,
      ajouterTexte: ajouterTexte,
      modifier: modifier,
      supprimer: supprimer,
      dupliquer: dupliquer,
      avancer: function (id) { deplacerRang(id, 1); },
      reculer: function (id) { deplacerRang(id, -1); },

      zoomer: zoomer,
      echelle: function (id) { return echelle(trouver(id)); },

      selection: function () { return trouver(selectionId); },
      selectionner: function (id) {
        selectionId = id;
        dessiner();
        signalerSelection(trouver(id));
      },
      calques: function () { return calques.slice(); },
      vide: function () { return !calques.length; },

      proportion: function (r) { ratio = r; dessiner(); prevenir(); },
      fond: function (c) { fond = c; dessiner(); prevenir(); memoriser(); },

      annuler: annuler,
      refaire: refaire,
      peutAnnuler: function () { return curseur > 0; },
      peutRefaire: function () { return curseur >= 0 && curseur < historique.length - 1; },
      /**
       * Garde ou retire le fond d'un calque image.
       *
       * Le choix appartient au calque, pas à la composition : on colle volontiers un
       * logo détouré sur une photo qui garde son cadre. Le détouré et l'original
       * cohabitent dans `sources`, si bien que revenir en arrière est immédiat et
       * n'appelle pas une seconde fois le service.
       */
      detourer: function (id, actif, cut) {
        var c = trouver(id);
        if (!c || c.type !== "image") return;
        if (cut && sources[c.srcId]) sources[c.srcId].cut = cut;
        if (c.detoure === actif) return;
        c.detoure = actif;
        c._sale = true;
        dessiner();
        prevenir();
        memoriser();
      },
      sources: function () { return sources; },
      analyser: analyser,
      exporter: exporter,
      dessiner: dessiner
    };
  }

  window.MurmureCanevas = { creer: creer, POLICES: CATALOGUE_POLICES };
})();
